import type { CardData, OpenOptions, PresendCardKind } from '../types';

const RECENT_CACHE_TTL_MS = 30_000;
const recentCache = new Map<string, { id: string; ts: number }>();

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * 基于内容 + 30s 时间窗的确定性 clientMsgId。
 * 幂等粒度：scope（preopen/postmsg）× 卡片内容 × userId × supplierId × 30s。
 * 零 DOM 依赖。
 */
export function buildClientMsgId(scope: string, payload: unknown): string {
  const text = `${scope}|${safeStringify(payload)}`;
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const key = (h >>> 0).toString(36);
  const now = Date.now();
  const hit = recentCache.get(key);
  if (hit && now - hit.ts < RECENT_CACHE_TTL_MS) return hit.id;
  for (const [k, v] of recentCache) {
    if (now - v.ts > RECENT_CACHE_TTL_MS) recentCache.delete(k);
  }
  const id = `card_${key}_${now.toString(36)}`;
  recentCache.set(key, { id, ts: now });
  return id;
}

/** 清空 cache（测试或 reset 用） */
export function _clearClientMsgIdCache(): void {
  recentCache.clear();
}

export function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

export function isNumericString(v: unknown): v is string {
  return typeof v === 'string' && /^-?\d+$/.test(v.trim());
}

/**
 * 校验卡片必填字段；失败抛 TypeError（不 emit，让调用方包装事件）。
 * 零 DOM 依赖。
 */
export function assertCardData(kind: PresendCardKind, data: unknown): asserts data is CardData {
  if (!data || typeof data !== 'object') {
    throw new TypeError(`[DajiCS] ${kind} 卡片 data 必须是对象`);
  }
  const obj = data as Record<string, unknown>;
  const missing: string[] = [];
  if (kind === 'product') {
    if (!isNonEmptyString(obj.title)) missing.push('title');
    if (!isNonEmptyString(obj.pic)) missing.push('pic');
    if (!isNonEmptyString(obj.id)) missing.push('id');
  } else if (kind === 'order') {
    if (!isNonEmptyString(obj.orderId)) missing.push('orderId');
    if (!isNonEmptyString(obj.goodsTitle)) missing.push('goodsTitle');
    if (!isNonEmptyString(obj.pic)) missing.push('pic');
    if (!isNonEmptyString(obj.price)) missing.push('price');
    if (!isNonEmptyString(obj.status)) {
      missing.push('status');
    } else if (isNumericString(obj.status)) {
      missing.push('status (收到数值/字符串数字，请先映射为文案如"待发货")');
    }
  } else if (kind === 'coupon') {
    if (!isNonEmptyString(obj.couponId)) missing.push('couponId');
    if (!isNonEmptyString(obj.couponName)) missing.push('couponName');
    if (!isNonEmptyString(obj.couponValue)) missing.push('couponValue');
    if (!isNonEmptyString(obj.couponType)) missing.push('couponType');
  }
  if (missing.length > 0) {
    throw new TypeError(`[DajiCS] ${kind} 卡片必填字段缺失或为空：${missing.join(', ')}`);
  }
}

/**
 * 把 OpenOptions.message 归一为 PresendMessage 形态。
 * 失败抛 TypeError（不 emit）。
 */
export function normalizePresendMessage(opts: OpenOptions): { kind: PresendCardKind; data: CardData } | null {
  if (!opts.message) return null;
  const m = opts.message;
  if (!m.kind) {
    throw new TypeError('[DajiCS] message.kind 必填（"product" | "order" | "coupon"）');
  }
  if (!m.data) {
    throw new TypeError(`[DajiCS] kind="${m.kind}" 时 message.data 必填`);
  }
  assertCardData(m.kind, m.data);
  return { kind: m.kind, data: m.data };
}

/** sendMsg 请求体构造（无 fetch 调用，由调用方决定 HTTP 实现） */
export interface SendMsgBody {
  groupId: string;
  objectName: string;
  content: string;
}

export const CARD_OBJECT_NAME = 'DAJI:Card';

export function buildSendCardBody(groupId: string, customType: PresendCardKind, card: CardData): SendMsgBody {
  return {
    groupId,
    objectName: CARD_OBJECT_NAME,
    content: JSON.stringify({ customType, data: card }),
  };
}
